import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { CONFIG } from './config';
import { colorForClient, prunePeers, upsertPeer, type Peer, CURSOR_THROTTLE_MS } from './collab/presence';
import { createBroadcastTransport, type SyncTransport } from './collab/transport';
import type { Vec } from './geometry/transform';
import {
  createAutosaver,
  clearCrashMirror,
  readCrashMirror,
  INITIAL_SAVE_STATE,
  type Autosaver,
  type SaveState,
} from './persist/autosave';
import { createStore, type BoardSummary, type SceneStore } from './persist/idb';
import { createCanvasMeasurer } from './render/measure';
import { toPngBlob } from './render/png';
import { toSvgString } from './render/svg';
import { newId, type ElementStyle } from './scene/elements';
import { boundTextMap } from './scene/normalize';
import { INITIAL_STATE, makeReducer, toBoard, type Tool } from './scene/reducer';
import { createSampleBoards } from './scene/samples';
import { backupFile, boardFile, safeFilename, toJsonText } from './scene/serialize';
import { DOC_VERSION, isText, type Board, type SlateElement } from './scene/types';
import { parseBoardFile } from './scene/validate';
import { BoardsDialog } from './ui/BoardsDialog';
import { DiagnosticsDialog } from './ui/DiagnosticsDialog';
import { Canvas } from './ui/Canvas';
import { ContentsPanel } from './ui/ContentsPanel';
import { downloadBlob, downloadText, pickJsonFile } from './ui/download';
import { IconDownload, IconHelp, IconList, IconMoon, IconSun } from './ui/icons';
import { Menu } from './ui/Menu';
import { MOD } from './ui/platform';
import { ImportResultDialog } from './ui/ImportResultDialog';
import { ShortcutsDialog } from './ui/ShortcutsDialog';
import { StatusBar } from './ui/StatusBar';
import { StylePanel } from './ui/StylePanel';
import { Toasts, type Toast } from './ui/Toasts';
import { Toolbar } from './ui/Toolbar';
import { TextOverlay } from './ui/TextOverlay';

type BootState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready' }
  | { readonly kind: 'failed'; readonly message: string };

const TYPE_LABELS: Record<string, string> = {
  rect: 'Rectangle',
  ellipse: 'Ellipse',
  diamond: 'Diamond',
  arrow: 'Arrow',
  freedraw: 'Drawing',
  text: 'Text',
};

type Theme = 'system' | 'light' | 'dark';
const THEME_KEY = 'slate-whiteboard:theme';
const HEARTBEAT_MS = 2500;
const PEER_STALE_MS = 8000;

function emptyBoard(name = 'Untitled board'): Board {
  const now = Date.now();
  return {
    id: newId(),
    name,
    createdAt: now,
    updatedAt: now,
    docVersion: DOC_VERSION,
    elements: [],
    view: { scrollX: 0, scrollY: 0, zoom: 1 },
    isSample: false,
  };
}

function readTheme(): Theme {
  try {
    const value = localStorage.getItem(THEME_KEY);
    if (value === 'light' || value === 'dark' || value === 'system') return value;
  } catch {
    // Storage unavailable; the system preference is a fine default.
  }
  return 'system';
}

export function App() {
  // The client id is per TAB and lives only in memory. Persisting it would make
  // two tabs of one profile indistinguishable, breaking both the last-write-wins
  // tiebreak and the cursors.
  const clientId = useRef(newId()).current;
  const measure = useMemo(() => createCanvasMeasurer(), []);
  const reducer = useMemo(
    () => makeReducer({ measureText: measure, clientId, now: () => Date.now() }),
    [measure, clientId],
  );
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  const [boot, setBoot] = useState<BootState>({ kind: 'loading' });
  const [store, setStore] = useState<SceneStore | null>(null);
  const [degraded, setDegraded] = useState<string | null>(null);
  const [boards, setBoards] = useState<readonly BoardSummary[]>([]);
  const [save, setSave] = useState<SaveState>(INITIAL_SAVE_STATE);
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const [peers, setPeers] = useState<readonly Peer[]>([]);
  const [peerCount, setPeerCount] = useState(0);
  const [dialog, setDialog] = useState<'none' | 'boards' | 'shortcuts' | 'diagnostics'>('none');
  const [importErrors, setImportErrors] = useState<{ filename: string; errors: readonly string[] } | null>(null);
  const [showContents, setShowContents] = useState(false);
  const [theme, setTheme] = useState<Theme>(readTheme);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const [panOverride, setPanOverride] = useState(false);
  const [syncAvailable, setSyncAvailable] = useState(false);

  const autosaver = useRef<Autosaver | null>(null);
  const transport = useRef<SyncTransport | null>(null);
  const boardRecord = useRef<Board | null>(null);
  const boardIdRef = useRef<string | null>(null);
  const clipboard = useRef<readonly SlateElement[]>([]);
  const lastSyncSeq = useRef(0);
  const lastCursorAt = useRef(0);
  const peerSeen = useRef(new Map<string, number>());

  boardIdRef.current = state.boardId;

  const toast = useCallback((kind: Toast['kind'], message: string, sticky = false) => {
    setToasts((current) => [...current.slice(-4), { id: newId(), kind, message, sticky }]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  /* ---------------- theme ---------------- */

  useEffect(() => {
    if (theme === 'system') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // Preference simply will not persist.
    }
  }, [theme]);

  useEffect(() => {
    document.title = CONFIG.appTitle;
  }, []);

  /* ---------------- boot ---------------- */

  const refreshBoards = useCallback(async (target: SceneStore) => {
    const list = await target.listBoards();
    setBoards(list);
    return list;
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await createStore();
        if (cancelled) return;
        setStore(result.store);
        setDegraded(result.degradedReason);

        let installId = await result.store.getMeta<string>('installId');
        if (!installId) {
          installId = newId();
          await result.store.setMeta('installId', installId);
        }

        let list = await result.store.listBoards();
        const seeded = await result.store.getMeta<boolean>('samplesSeeded');
        if (!seeded && CONFIG.seedSamples && list.length === 0) {
          for (const sample of createSampleBoards()) await result.store.saveBoard(sample);
          await result.store.setMeta('samplesSeeded', true);
          list = await result.store.listBoards();
        }

        const lastBoardId = await result.store.getMeta<string>('lastBoardId');
        let board =
          (lastBoardId ? await result.store.loadBoard(lastBoardId) : null) ??
          (list[0] ? await result.store.loadBoard(list[0].id) : null);
        if (!board) {
          board = emptyBoard();
          await result.store.saveBoard(board);
          list = await result.store.listBoards();
        }

        const mirror = readCrashMirror();
        if (mirror && mirror.id === board.id && mirror.updatedAt > board.updatedAt) {
          board = mirror;
          toast('info', 'Recovered changes that had not finished saving.');
        } else {
          clearCrashMirror();
        }

        if (cancelled) return;
        setBoards(list);
        boardRecord.current = board;
        dispatch({ t: 'loadBoard', board });
        setBoot({ kind: 'ready' });

        if (result.degradedReason) {
          toast('error', result.degradedReason, true);
        }
      } catch (error) {
        if (cancelled) return;
        setBoot({
          kind: 'failed',
          message: error instanceof Error ? error.message : 'The editor failed to start.',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  /* ---------------- autosave ---------------- */

  useEffect(() => {
    if (!store) return undefined;
    const saver = createAutosaver(store, setSave);
    autosaver.current = saver;
    return () => {
      saver.dispose();
      autosaver.current = null;
    };
  }, [store]);

  useEffect(() => {
    if (boot.kind !== 'ready' || !state.boardId) return;
    const board = toBoard(state, boardRecord.current);
    boardRecord.current = board;
    autosaver.current?.schedule(board);
    // Only geometry, view and name are persisted; the rest of the state is UI.
  }, [state.elements, state.view, state.boardName, state.boardId, boot.kind]);

  // No writes mid-gesture: a whole-board snapshot during a drag is what makes a
  // canvas feel sticky. The save happens the moment the gesture ends.
  useEffect(() => {
    if (state.interaction) autosaver.current?.suspend();
    else autosaver.current?.resume();
  }, [state.interaction]);

  useEffect(() => {
    if (!store || !state.boardId) return;
    void store.setMeta('lastBoardId', state.boardId);
  }, [store, state.boardId]);

  useEffect(() => {
    const onHide = () => {
      autosaver.current?.mirror();
      void autosaver.current?.flush();
      if (boardIdRef.current) {
        transport.current?.post({ kind: 'bye', boardId: boardIdRef.current, clientId });
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') onHide();
    };
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [clientId]);

  /* ---------------- same-device sync ---------------- */

  useEffect(() => {
    const channel = createBroadcastTransport(CONFIG.syncChannel);
    transport.current = channel;
    setSyncAvailable(channel.available);
    const markSeen = (peerId: string) => {
      peerSeen.current.set(peerId, Date.now());
      setPeerCount(peerSeen.current.size);
    };
    const unsubscribe = channel.subscribe((message) => {
      if (message.clientId === clientId) return;
      if (message.boardId !== boardIdRef.current) return;
      switch (message.kind) {
        case 'elements':
          markSeen(message.clientId);
          dispatch({ t: 'applyRemote', elements: message.elements });
          break;
        case 'cursor':
          markSeen(message.clientId);
          setPeers((current) => upsertPeer(current, message.clientId, message.x, message.y, Date.now()));
          break;
        case 'hello':
          markSeen(message.clientId);
          break;
        case 'bye':
          peerSeen.current.delete(message.clientId);
          setPeerCount(peerSeen.current.size);
          setPeers((current) => current.filter((p) => p.clientId !== message.clientId));
          break;
      }
    });

    const heartbeat = setInterval(() => {
      const boardId = boardIdRef.current;
      if (boardId) channel.post({ kind: 'hello', boardId, clientId });
      const now = Date.now();
      let removed = false;
      for (const [peerId, seen] of peerSeen.current) {
        if (now - seen > PEER_STALE_MS) {
          peerSeen.current.delete(peerId);
          removed = true;
        }
      }
      if (removed) setPeerCount(peerSeen.current.size);
      setPeers((current) => {
        const pruned = prunePeers(current, now);
        return pruned.length === current.length ? current : pruned;
      });
    }, HEARTBEAT_MS);

    return () => {
      clearInterval(heartbeat);
      unsubscribe();
      channel.close();
      transport.current = null;
      setSyncAvailable(false);
    };
  }, [clientId]);

  // Broadcast only what changed, and only when the change was made here.
  useEffect(() => {
    if (state.lastChange.origin !== 'local') return;
    if (state.lastChange.seq === lastSyncSeq.current) return;
    lastSyncSeq.current = state.lastChange.seq;
    const boardId = state.boardId;
    if (!boardId) return;
    const ids = new Set(state.lastChange.ids);
    const payload = state.elements.filter((el) => ids.has(el.id));
    if (payload.length > 0) {
      transport.current?.post({ kind: 'elements', boardId, clientId, elements: payload });
    }
  }, [state.lastChange, state.elements, state.boardId, clientId]);

  // Peers appear when the board changes, so clear stale cursors on switch.
  useEffect(() => {
    peerSeen.current.clear();
    setPeerCount(0);
    setPeers([]);
  }, [state.boardId]);

  const handleViewportChange = useCallback((size: { width: number; height: number }) => {
    setViewport((previous) =>
      previous.width === size.width && previous.height === size.height ? previous : size,
    );
  }, []);

  const onPointerWorld = useCallback(
    (world: Vec) => {
      const now = Date.now();
      if (now - lastCursorAt.current < CURSOR_THROTTLE_MS) return;
      lastCursorAt.current = now;
      const boardId = boardIdRef.current;
      if (!boardId) return;
      transport.current?.post({ kind: 'cursor', boardId, clientId, x: world.x, y: world.y });
    },
    [clientId],
  );

  /* ---------------- board operations ---------------- */

  const openBoard = useCallback(
    async (id: string) => {
      if (!store) return;
      await autosaver.current?.flush();
      const board = await store.loadBoard(id);
      if (!board) {
        toast('error', 'That board is no longer in local storage.');
        await refreshBoards(store);
        return;
      }
      boardRecord.current = board;
      dispatch({ t: 'loadBoard', board });
      setDialog('none');
    },
    [store, toast, refreshBoards],
  );

  const createBoard = useCallback(async () => {
    if (!store) return;
    await autosaver.current?.flush();
    const board = emptyBoard();
    await store.saveBoard(board);
    await refreshBoards(store);
    boardRecord.current = board;
    dispatch({ t: 'loadBoard', board });
    setDialog('none');
    toast('success', 'New board created.');
  }, [store, refreshBoards, toast]);

  const duplicateBoard = useCallback(
    async (id: string) => {
      if (!store) return;
      const source = await store.loadBoard(id);
      if (!source) return;
      const copy: Board = {
        ...source,
        id: newId(),
        name: `${source.name} copy`,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        isSample: false,
      };
      await store.saveBoard(copy);
      await refreshBoards(store);
      toast('success', `Duplicated as "${copy.name}".`);
    },
    [store, refreshBoards, toast],
  );

  const deleteBoard = useCallback(
    async (id: string) => {
      if (!store) return;
      await store.deleteBoard(id);
      const list = await refreshBoards(store);
      toast('success', 'Board deleted.');
      if (id !== state.boardId) return;
      const next = list[0] ? await store.loadBoard(list[0].id) : null;
      if (next) {
        boardRecord.current = next;
        dispatch({ t: 'loadBoard', board: next });
      } else {
        const fresh = emptyBoard();
        await store.saveBoard(fresh);
        await refreshBoards(store);
        boardRecord.current = fresh;
        dispatch({ t: 'loadBoard', board: fresh });
      }
    },
    [store, refreshBoards, toast, state.boardId],
  );

  const deleteSamples = useCallback(async () => {
    if (!store) return;
    const list = await store.listBoards();
    const samples = list.filter((b) => b.isSample);
    for (const sample of samples) await store.deleteBoard(sample.id);
    // Remember the choice so a reload does not bring them back.
    await store.setMeta('samplesSeeded', true);
    const remaining = await refreshBoards(store);
    toast('success', `Deleted ${samples.length} sample board${samples.length === 1 ? '' : 's'}.`);
    if (!samples.some((s) => s.id === state.boardId)) return;
    const next = remaining[0] ? await store.loadBoard(remaining[0].id) : null;
    if (next) {
      boardRecord.current = next;
      dispatch({ t: 'loadBoard', board: next });
    } else {
      const fresh = emptyBoard();
      await store.saveBoard(fresh);
      await refreshBoards(store);
      boardRecord.current = fresh;
      dispatch({ t: 'loadBoard', board: fresh });
    }
  }, [store, refreshBoards, toast, state.boardId]);

  /* ---------------- import and export ---------------- */

  const liveElements = useMemo(() => state.elements.filter((el) => !el.isDeleted), [state.elements]);

  const canvasColor = useCallback(() => {
    const value = getComputedStyle(document.documentElement).getPropertyValue('--canvas').trim();
    return value.length > 0 ? value : '#ffffff';
  }, []);

  const exportSvg = useCallback(() => {
    if (liveElements.length === 0) {
      toast('error', 'This board is empty, so there is nothing to export yet.');
      return;
    }
    try {
      const svg = toSvgString(liveElements, measure, { background: canvasColor() });
      downloadText(safeFilename(state.boardName, 'svg'), svg, 'image/svg+xml');
      toast('success', `Exported ${safeFilename(state.boardName, 'svg')}.`);
    } catch {
      toast('error', 'The SVG export failed. Nothing was written.');
    }
  }, [liveElements, measure, state.boardName, toast, canvasColor]);

  const exportPng = useCallback(async () => {
    if (liveElements.length === 0) {
      toast('error', 'This board is empty, so there is nothing to export yet.');
      return;
    }
    try {
      const result = await toPngBlob(liveElements, measure, { scale: 2, background: canvasColor() });
      downloadBlob(safeFilename(state.boardName, 'png'), result.blob);
      toast(
        'success',
        result.clampedFrom === null
          ? `Exported ${safeFilename(state.boardName, 'png')} at ${result.width} by ${result.height} pixels.`
          : `Exported at ${result.scale.toFixed(2)}x instead of ${result.clampedFrom}x to stay within this browser's canvas limit.`,
      );
    } catch (error) {
      toast('error', error instanceof Error ? error.message : 'The PNG export failed.');
    }
  }, [liveElements, measure, state.boardName, toast, canvasColor]);

  const exportJson = useCallback(() => {
    const board = toBoard(state, boardRecord.current);
    downloadText(
      safeFilename(state.boardName, 'json'),
      toJsonText(boardFile(board)),
      'application/json',
    );
    toast('success', `Exported ${safeFilename(state.boardName, 'json')}.`);
  }, [state, toast]);

  const exportAll = useCallback(async () => {
    if (!store) return;
    await autosaver.current?.flush();
    const all = await store.loadAllBoards();
    if (all.length === 0) {
      toast('error', 'There are no boards to back up.');
      return;
    }
    const stamp = new Date().toISOString().slice(0, 10);
    downloadText(
      `slate-whiteboard-backup-${stamp}.json`,
      toJsonText(backupFile(all)),
      'application/json',
    );
    toast('success', `Backed up ${all.length} board${all.length === 1 ? '' : 's'}.`);
  }, [store, toast]);

  const importJson = useCallback(async () => {
    if (!store) return;
    let picked;
    try {
      picked = await pickJsonFile(CONFIG.maxImportBytes);
    } catch (error) {
      toast('error', error instanceof Error ? error.message : 'That file could not be read.');
      return;
    }
    if (!picked) return;

    const result = parseBoardFile(picked.text);
    if (!result.ok) {
      setImportErrors({ filename: picked.name, errors: result.errors });
      return;
    }

    const existing = new Set((await store.listBoards()).map((b) => b.id));
    let first: Board | null = null;
    for (const candidate of result.boards) {
      // Never overwrite an existing board: a colliding id becomes a new board.
      const board: Board = existing.has(candidate.id)
        ? { ...candidate, id: newId(), name: `${candidate.name} (imported)` }
        : candidate;
      await store.saveBoard(board);
      existing.add(board.id);
      if (!first) first = board;
    }
    await refreshBoards(store);
    if (first) {
      boardRecord.current = first;
      dispatch({ t: 'loadBoard', board: first });
    }
    setDialog('none');
    const count = result.boards.length;
    toast('success', `Imported ${count} board${count === 1 ? '' : 's'}.`);
    for (const repair of result.repairs) toast('info', repair);
  }, [store, refreshBoards, toast]);

  /* ---------------- keyboard ---------------- */

  const selectedIds = state.selectedIds;
  const editingText = useMemo(() => {
    if (!state.editingTextId) return null;
    const el = state.elements.find((e) => e.id === state.editingTextId);
    return el && isText(el) ? el : null;
  }, [state.editingTextId, state.elements]);

  const copySelection = useCallback(() => {
    const ids = new Set(selectedIds);
    const labels = boundTextMap(state.elements);
    for (const id of selectedIds) {
      const label = labels.get(id);
      if (label) ids.add(label.id);
    }
    clipboard.current = state.elements.filter((el) => ids.has(el.id) && !el.isDeleted);
    return clipboard.current.length;
  }, [selectedIds, state.elements]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        !!target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (typing || editingText) return;
      if (dialog !== 'none' || importErrors) return;

      const mod = event.metaKey || event.ctrlKey;

      if (event.code === 'Space' && !event.repeat) {
        setPanOverride(true);
        event.preventDefault();
        return;
      }

      if (mod) {
        switch (event.key.toLowerCase()) {
          case 'z':
            event.preventDefault();
            dispatch(event.shiftKey ? { t: 'redo' } : { t: 'undo' });
            return;
          case 'y':
            event.preventDefault();
            dispatch({ t: 'redo' });
            return;
          case 'a':
            event.preventDefault();
            dispatch({ t: 'selectAll' });
            return;
          case 'd':
            event.preventDefault();
            dispatch({ t: 'duplicate' });
            return;
          case 'c': {
            const n = copySelection();
            if (n > 0) toast('info', `Copied ${n} element${n === 1 ? '' : 's'}.`);
            return;
          }
          case 'x': {
            const n = copySelection();
            if (n > 0) {
              dispatch({ t: 'deleteSelected' });
              toast('info', `Cut ${n} element${n === 1 ? '' : 's'}.`);
            }
            return;
          }
          case 'v':
            if (clipboard.current.length > 0) {
              event.preventDefault();
              dispatch({ t: 'pasteElements', elements: clipboard.current });
            }
            return;
          case 's':
            event.preventDefault();
            if (event.shiftKey) exportSvg();
            else exportJson();
            return;
          case 'p':
            if (event.shiftKey) {
              event.preventDefault();
              void exportPng();
            }
            return;
          case '=':
          case '+':
            event.preventDefault();
            dispatch({
              t: 'zoomBy',
              factor: 1.2,
              anchor: { x: viewport.width / 2, y: viewport.height / 2 },
            });
            return;
          case '-':
            event.preventDefault();
            dispatch({
              t: 'zoomBy',
              factor: 1 / 1.2,
              anchor: { x: viewport.width / 2, y: viewport.height / 2 },
            });
            return;
          case '0':
            event.preventDefault();
            dispatch({ t: 'zoomReset' });
            return;
          case '/':
            event.preventDefault();
            setShowContents((v) => !v);
            return;
          default:
            return;
        }
      }

      if (event.key === '?') {
        event.preventDefault();
        setDialog('shortcuts');
        return;
      }

      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (selectedIds.length > 0) {
          event.preventDefault();
          dispatch({ t: 'deleteSelected' });
        }
        return;
      }

      if (event.key === 'Escape') {
        if (state.tool !== 'select') dispatch({ t: 'setTool', tool: 'select' });
        else dispatch({ t: 'select', ids: [] });
        return;
      }

      if (event.key === 'Enter' && selectedIds.length === 1) {
        const el = state.elements.find((e) => e.id === selectedIds[0]);
        if (!el) return;
        event.preventDefault();
        if (isText(el)) dispatch({ t: 'startEditText', id: el.id });
        else {
          const center = { x: el.x + el.width / 2, y: el.y + el.height / 2 };
          dispatch({ t: 'doubleClick', world: center });
        }
        return;
      }

      if (event.key.startsWith('Arrow')) {
        if (selectedIds.length === 0) return;
        event.preventDefault();
        const step = event.shiftKey ? 12 : 1;
        const dx = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0;
        const dy = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0;
        dispatch({ t: 'nudge', dx, dy });
        return;
      }

      if (event.key === '[' || event.key === ']') {
        if (selectedIds.length === 0) return;
        event.preventDefault();
        dispatch({ t: 'zOrder', dir: event.key === ']' ? 'front' : 'back' });
        return;
      }

      if (event.key === '1' && event.shiftKey) {
        event.preventDefault();
        dispatch({ t: 'zoomToFit', viewport });
        return;
      }

      const toolKeys: Record<string, Tool> = {
        v: 'select',
        r: 'rect',
        o: 'ellipse',
        d: 'diamond',
        a: 'arrow',
        l: 'line',
        p: 'freedraw',
        t: 'text',
        e: 'eraser',
        h: 'pan',
      };
      const tool = toolKeys[event.key.toLowerCase()];
      if (tool) {
        event.preventDefault();
        dispatch({ t: 'setTool', tool });
        return;
      }
      if (event.key.toLowerCase() === 'q') {
        event.preventDefault();
        dispatch({ t: 'toggleToolLock' });
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'Space') setPanOverride(false);
    };

    // Losing focus mid-drag would otherwise leave pan mode stuck on.
    const onBlur = () => setPanOverride(false);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [
    dialog,
    importErrors,
    editingText,
    selectedIds,
    state.tool,
    state.elements,
    viewport,
    copySelection,
    exportJson,
    exportPng,
    exportSvg,
    toast,
  ]);

  /* ---------------- render ---------------- */

  if (boot.kind === 'loading') {
    return (
      <div className="boot">
        <div className="card">
          <div className="spinner" aria-hidden="true" />
          <p role="status">Opening your boards from this browser&rsquo;s local storage&hellip;</p>
        </div>
      </div>
    );
  }

  if (boot.kind === 'failed') {
    return (
      <div className="boot">
        <div className="card" style={{ maxWidth: 460 }}>
          <h1 style={{ fontSize: 17 }}>The editor could not start</h1>
          <p>{boot.message}</p>
          <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }

  const labels = boundTextMap(state.elements);
  const labelText = new Map<string, string>();
  for (const [containerId, text] of labels) labelText.set(containerId, text.text);
  const selectedElements = state.elements.filter((el) => selectedIds.includes(el.id));
  const hasTextSelected = selectedElements.some((el) => isText(el));
  const selectionLabel =
    selectedElements.length === 1 && selectedElements[0]
      ? TYPE_LABELS[selectedElements[0].type] ?? 'Element'
      : `${selectedElements.length} elements`;
  const styleForPanel: ElementStyle =
    selectedElements.length === 1 && selectedElements[0]
      ? {
          ...state.style,
          strokeColor: selectedElements[0].strokeColor,
          backgroundColor: selectedElements[0].backgroundColor,
          fillStyle: selectedElements[0].fillStyle,
          strokeWidth: selectedElements[0].strokeWidth,
          strokeStyle: selectedElements[0].strokeStyle,
          opacity: selectedElements[0].opacity,
          fontSize: isText(selectedElements[0]) ? selectedElements[0].fontSize : state.style.fontSize,
          fontFamily: isText(selectedElements[0]) ? selectedElements[0].fontFamily : state.style.fontFamily,
          textAlign: isText(selectedElements[0]) ? selectedElements[0].textAlign : state.style.textAlign,
        }
      : state.style;

  return (
    <div className="app">
      <a className="skip-link" href="#board-canvas">
        Skip to the canvas
      </a>

      <header className="topbar">
        <span className="brand">
          <span className="mark" aria-hidden="true" />
          {CONFIG.appTitle}
        </span>
        <label className="sr-only" htmlFor="board-name">
          Board name
        </label>
        <input
          id="board-name"
          className="board-name-input"
          value={state.boardName}
          maxLength={200}
          onChange={(event) => dispatch({ t: 'renameBoard', name: event.target.value })}
          onBlur={(event) => {
            if (event.target.value.trim() === '') dispatch({ t: 'renameBoard', name: 'Untitled board' });
          }}
        />
        {state.isSample ? <span className="badge">Sample</span> : null}

        <button type="button" className="btn btn-quiet" onClick={() => setDialog('boards')}>
          Boards
          <span style={{ color: 'var(--text-faint)' }}>{boards.length}</span>
        </button>

        <Menu
          label="Export"
          icon={<IconDownload />}
          items={[
            { label: 'SVG', hint: `${MOD}+Shift+S`, onSelect: exportSvg },
            { label: 'PNG at 2x', hint: `${MOD}+Shift+P`, onSelect: () => void exportPng() },
            { label: 'JSON', hint: `${MOD}+S`, onSelect: exportJson },
            { label: 'Backup of all boards', onSelect: () => void exportAll(), separated: true },
            { label: 'Import JSON\u2026', onSelect: () => void importJson() },
          ]}
        />

        <span className="topbar-spacer" />

        <span className="topbar-group">
          <button
            type="button"
            className="btn btn-icon"
            aria-pressed={showContents}
            aria-label="Board contents"
            title="Board contents"
            onClick={() => setShowContents((v) => !v)}
          >
            <IconList />
          </button>
          <button
            type="button"
            className="btn btn-icon"
            aria-label={`Theme: ${theme}. Switch theme.`}
            title={`Theme: ${theme}`}
            onClick={() => setTheme(theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system')}
          >
            {theme === 'dark' ? <IconMoon /> : <IconSun />}
          </button>
          <button
            type="button"
            className="btn btn-icon"
            onClick={() => setDialog('shortcuts')}
            aria-label="Keyboard shortcuts"
            title="Keyboard shortcuts"
          >
            <IconHelp />
          </button>
        </span>
      </header>

      {degraded ? (
        <div className="banner" role="alert">
          <span>
            <strong>Changes are not being saved.</strong> {degraded} Export a JSON copy before closing
            this tab.
          </span>
          <button type="button" className="btn btn-sm" onClick={exportJson}>
            Export JSON now
          </button>
        </div>
      ) : null}

      {save.quotaBlocked ? (
        <div className="banner" role="alert">
          <span>
            <strong>This browser is out of storage space.</strong> Autosave has stopped. Export a backup,
            then delete a board you no longer need.
          </span>
          <button type="button" className="btn btn-sm" onClick={() => void exportAll()}>
            Export backup
          </button>
        </div>
      ) : null}

      <main className="workspace">
        <Toolbar
          tool={state.tool}
          lockTool={state.lockTool}
          onSelectTool={(tool) => dispatch({ t: 'setTool', tool })}
          onToggleLock={() => dispatch({ t: 'toggleToolLock' })}
        />

        <Canvas
          elements={state.elements}
          selectedIds={selectedIds}
          view={state.view}
          tool={state.tool}
          marquee={state.marquee}
          hoverBindTargetId={state.hoverBindTargetId}
          peers={peers}
          measure={measure}
          interacting={state.interaction !== null}
          panOverride={panOverride}
          dispatch={dispatch}
          onPointerWorld={onPointerWorld}
          onViewportChange={handleViewportChange}
        />


        {peerCount > 0 ? (
          <div className="floating peer-badges" role="status">
            <span className="peer-dot" style={{ background: colorForClient(clientId) }} aria-hidden="true" />
            {peerCount} other window{peerCount === 1 ? '' : 's'} editing this board
          </div>
        ) : null}

        {editingText ? (
          <TextOverlay
            element={editingText}
            view={state.view}
            onChange={(text) => dispatch({ t: 'editTextValue', id: editingText.id, text })}
            onCommit={() => dispatch({ t: 'endEditText' })}
          />
        ) : null}

        {liveElements.length === 0 && !state.interaction ? (
          <div className="empty-state">
            <div className="card">
              <h2>This board is empty</h2>
              <p>
                Pick a shape and drag on the canvas. Draw two shapes, then use the arrow tool to connect
                them: the arrow stays attached when you move either one.
              </p>
              <div className="actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => dispatch({ t: 'setTool', tool: 'rect' })}
                >
                  Start with a rectangle
                </button>
                <button type="button" className="btn" onClick={() => setDialog('shortcuts')}>
                  Keyboard shortcuts
                </button>
                <button type="button" className="btn" onClick={() => void importJson()}>
                  Import JSON
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="right-rail">
          <StylePanel
            style={styleForPanel}
            hasSelection={selectedIds.length > 0}
            hasTextSelected={hasTextSelected}
            selectionLabel={selectionLabel}
            onChange={(patch) => dispatch({ t: 'setStyle', patch })}
          />
          {showContents ? (
            <ContentsPanel
              elements={state.elements}
              selectedIds={selectedIds}
              labels={labelText}
              onSelect={(id) => {
                dispatch({ t: 'select', ids: [id] });
                dispatch({ t: 'scrollToSelection', viewport });
              }}
              onClose={() => setShowContents(false)}
            />
          ) : null}
        </div>
      </main>

      <StatusBar
        save={save}
        storageMode={store?.mode ?? 'memory'}
        elementCount={liveElements.length}
        selectionCount={selectedIds.length}
        peerCount={peerCount}
        syncAvailable={syncAvailable}
        announcement={state.announcement}
        onShowDiagnostics={() => setDialog('diagnostics')}
        zoom={state.view.zoom}
        onZoom={(factor) =>
          dispatch({
            t: 'zoomBy',
            factor,
            anchor: { x: viewport.width / 2, y: viewport.height / 2 },
          })
        }
        onZoomReset={() => dispatch({ t: 'zoomReset' })}
        onZoomToFit={() => dispatch({ t: 'zoomToFit', viewport })}
      />

      {dialog === 'boards' ? (
        <BoardsDialog
          boards={boards}
          currentId={state.boardId}
          onOpen={(id) => void openBoard(id)}
          onCreate={() => void createBoard()}
          onDuplicate={(id) => void duplicateBoard(id)}
          onDelete={(id) => void deleteBoard(id)}
          onDeleteSamples={() => void deleteSamples()}
          onImport={() => void importJson()}
          onExportAll={() => void exportAll()}
          onClose={() => setDialog('none')}
        />
      ) : null}

      {dialog === 'shortcuts' ? <ShortcutsDialog onClose={() => setDialog('none')} /> : null}

      {dialog === 'diagnostics' ? (
        <DiagnosticsDialog
          elementCount={liveElements.length}
          totalCount={state.elements.length}
          zoom={state.view.zoom}
          storageMode={store?.mode ?? 'memory'}
          syncAvailable={syncAvailable}
          boardName={state.boardName}
          onClose={() => setDialog('none')}
        />
      ) : null}

      {importErrors ? (
        <ImportResultDialog
          filename={importErrors.filename}
          errors={importErrors.errors}
          onClose={() => setImportErrors(null)}
        />
      ) : null}

      <Toasts toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
